import { useEffect, useState } from 'react';
import { Button, Checkbox, Group, Modal, Select, SimpleGrid, Stack, Text, UnstyledButton } from '@mantine/core';
import { REQUEST_EXPORT_COLUMNS } from '@parts/shared';
import { useI18n } from '../../i18n';
import { IconDownload } from '../../icons';
import { getPref, setPref } from '../../settings';
import classes from './RequestsView.module.css';

const PREF_COLS = 'export:requests:cols';
const ALL = '__all';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Номера запросов для выбора. */
  requests: string[];
}

/**
 * Выгрузка «Текущих запросов» в Excel: один запрос или все, и какие столбцы.
 * Выбор столбцов запоминается для пользователя.
 */
export function ExportDialog({ opened, onClose, requests }: Props) {
  const { t, tx } = useI18n();
  const all = REQUEST_EXPORT_COLUMNS.map((c) => c.key);
  const [req, setReq] = useState<string>(ALL);
  const [cols, setCols] = useState<string[]>(() => getPref<string[]>(PREF_COLS, all));

  useEffect(() => {
    if (opened) setCols(getPref<string[]>(PREF_COLS, all));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const change = (next: string[]) => {
    // «№ запроса / OEM» — всегда
    const fixed = REQUEST_EXPORT_COLUMNS.filter((c) => c.fixed).map((c) => c.key);
    const value = [...new Set([...fixed, ...next])];
    setCols(value);
    setPref(PREF_COLS, value);
  };

  const download = () => {
    const params = new URLSearchParams();
    if (req !== ALL) params.set('req', req);
    if (cols.length < all.length) params.set('cols', cols.join(','));
    const query = params.toString();
    window.location.href = `/api/export/requests.xlsx${query ? `?${query}` : ''}`;
    onClose();
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t('Выгрузка в Excel')} size="lg">
      <Stack gap="md">
        <Select
          label={t('Какие запросы')}
          value={req}
          onChange={(v) => setReq(v ?? ALL)}
          data={[{ value: ALL, label: t('Все запросы') }, ...requests.map((no) => ({ value: no, label: `№ ${no}` }))]}
          searchable
          allowDeselect={false}
          nothingFoundMessage={t('Не найдено')}
        />
        <div>
          <Group justify="space-between" mb={8}>
            <Text size="sm" fw={500}>
              {t('Столбцы')}
            </Text>
            <Group gap={4}>
              <UnstyledButton className={classes.ghostBtn} onClick={() => change(all)}>
                {t('Выбрать все')}
              </UnstyledButton>
              <UnstyledButton className={classes.ghostBtn} onClick={() => change([])}>
                {t('Снять все')}
              </UnstyledButton>
            </Group>
          </Group>
          <Checkbox.Group value={cols} onChange={change}>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={8} verticalSpacing={8}>
              {REQUEST_EXPORT_COLUMNS.map((c) => (
                <Checkbox key={c.key} value={c.key} label={tx(c.title)} disabled={c.fixed} />
              ))}
            </SimpleGrid>
          </Checkbox.Group>
          <Text size="xs" c="dimmed" mt={10}>
            {t('Выбор столбцов запоминается.')}
          </Text>
        </div>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('Отмена')}
          </Button>
          <Button leftSection={<IconDownload size={15} />} onClick={download}>
            {t('Выгрузить')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
